import { Formula, Vehicle } from '@otw/models'
import { matchFormula } from 'hooks/graybook/matchFormula'
import { calculatePrice } from '@/hooks/graybook/useFormula'
import { getDealQuality } from 'hooks/graybook/useCalculateDeal'

export type SafetyColor = 'success.main' | 'warning.main' | 'error.main' | 'text.secondary'

export interface SafetyColors {
  cameras: SafetyColor
  sensors: SafetyColor
  antiTheft: SafetyColor
}

export interface TimelineInfo {
  isInPackage: boolean
  isDelivered: boolean
}

export const DEFAULT_SAFETY_COLORS: SafetyColors = {
  cameras: 'text.secondary',
  sensors: 'text.secondary',
  antiTheft: 'text.secondary',
}

export interface InventoryV3Row {
  id: string
  /** Full source document — used by dialogs, detail panel and the actions drawer. */
  vehicle: Vehicle

  // Identity
  stock_number: string
  vin: string
  entered_by: string
  date_added: string | null
  date_available: string | null

  // Basic vehicle info
  year: number | null
  make: string | null
  model: string | null
  size: string | null
  type: string | null
  miles: number | null
  fuel: Vehicle['fuel']
  condition: Vehicle['condition']
  tier: Vehicle['tier']
  color: string | null
  rear_wheel: Vehicle['rear_wheel']

  // Money
  optic_list_price: number | null
  seller_asking_price: number | null
  profit: number | null
  profit_realized: boolean
  lien_payoff_amount: number | null
  repair_cost: number | null
  estimated_repair_cost: number | null
  shipping_cost: number | null

  // Sale
  status: Vehicle['status']
  sold_date: string | null
  who: string | null
  bought_from: string | null
  sold_to_name: string
  sold_by_name: string
  selling_to_name: string

  // Contact / notes
  location: string | null
  phone_number: string | null
  email: string | null
  truck_notes: string | null
  follow_up_notes: string | null
  link: string | null
  /** US state abbreviation for where the title copy is from. */
  title_state: string | null

  // Inspection / equipment
  mechanical_status: Vehicle['mechanical_status']
  body_status: Vehicle['body_status']
  cameras: Vehicle['cameras']
  shelves: Vehicle['shelves']
  gwvr_over_10k: Vehicle['gwvr_over_10k']

  // Booleans
  show_on_website: boolean
  favorite: boolean
  paid_for: boolean
  ready_for_sale: boolean
  funded: boolean
  size_confirmed: boolean
  refinance: boolean

  /** Complete / Incomplete from pickup_location, or N/A for refinance (no pickup address). */
  pickup_address: 'Complete' | 'Incomplete' | 'N/A'
  /** 'Ready' | 'Not Ready' — derived from the ready_for_transport sub-object. */
  ready_for_transport: 'Ready' | 'Not Ready'
  /** 'Yes' | 'No' | 'N/A' — N/A when not purchased for inventory or when refinanced. */
  delivered_to_customer: 'Yes' | 'No' | 'N/A'

  // Precomputed gray book values (never computed inside a cell renderer)
  grayBookPrice: number | null
  ltv: number | null
  dealQuality: string | null

  // Precomputed transporter / logistics state
  safety: SafetyColors
  isInPackage: boolean
  isDelivered: boolean
}

interface BuildInventoryV3RowsParams {
  vehicles: Vehicle[]
  formulas: Formula[] | undefined
  /** Vehicle id → precomputed safety icon colors from the transporter package. */
  safetyByVehicleId: Map<string, SafetyColors>
  /** Vehicle id → precomputed package / delivery state. */
  timelineByVehicleId: Map<string, TimelineInfo>
  isOwner: boolean
}

interface GrayBookResult {
  grayBookPrice: number | null
  ltv: number | null
  dealQuality: string | null
}

const EMPTY_GRAY_BOOK: GrayBookResult = { grayBookPrice: null, ltv: null, dealQuality: null }

const isPickupAddressComplete = (vehicle: Vehicle): boolean => {
  const pickup = vehicle.pickup_location
  if (!pickup) return false
  return Boolean(
    pickup.address?.trim() &&
      pickup.city?.trim() &&
      pickup.state?.trim() &&
      pickup.zip?.trim() &&
      pickup.contact_name?.trim() &&
      pickup.contact_phone?.trim(),
  )
}

const calculateGrayBook = (vehicle: Vehicle, formulas: Formula[] | undefined, isOwner: boolean): GrayBookResult => {
  if (!formulas?.length) return EMPTY_GRAY_BOOK

  const { make, model, type, size, miles, year, fuel, optic_list_price, seller_asking_price } = vehicle
  if (!make || !model || !type || !size || !miles || !year || !fuel) return EMPTY_GRAY_BOOK

  const matchedFormula = matchFormula(formulas, vehicle)
  if (!matchedFormula?.rules?.length) return EMPTY_GRAY_BOOK

  const { price: grayBookPrice } = calculatePrice({
    input: { year: Number(year), mileage: Number(String(miles).replace(/,/g, '')) },
    formula: matchedFormula,
    rules: matchedFormula.rules,
  })
  if (!grayBookPrice) return EMPTY_GRAY_BOOK

  let ltv: number | null = null
  const listPrice = optic_list_price == null ? NaN : Number(optic_list_price)
  if (!isNaN(listPrice) && listPrice > 0) {
    ltv = (listPrice / grayBookPrice) * 100
  }

  let dealQuality: string | null = null
  const askingPrice = seller_asking_price == null ? NaN : Number(seller_asking_price)
  if (!isNaN(askingPrice)) {
    const wholesalePrice = grayBookPrice * (1 - (matchedFormula.wholesaleDiscount || 0) / 100)
    dealQuality = getDealQuality(askingPrice, wholesalePrice, isOwner)
  }

  return { grayBookPrice, ltv, dealQuality }
}

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return isNaN(parsed) ? null : parsed
}

export const buildInventoryV3Rows = ({
  vehicles,
  formulas,
  safetyByVehicleId,
  timelineByVehicleId,
  isOwner,
}: BuildInventoryV3RowsParams): InventoryV3Row[] =>
  vehicles.map(vehicle => {
    const { grayBookPrice, ltv, dealQuality } = calculateGrayBook(vehicle, formulas, isOwner)
    const timeline = timelineByVehicleId.get(vehicle.id)
    const isNotApplicableDelivery = vehicle.purchased_for_inventory !== true || vehicle.refinance === true

    return {
      id: vehicle.id,
      vehicle,

      stock_number: vehicle.stock_number ?? '',
      vin: vehicle.vin ?? '',
      entered_by: vehicle.entered_by ?? '',
      date_added: vehicle.date_added ?? null,
      date_available: vehicle.date_available ?? null,

      year: vehicle.year ?? null,
      make: vehicle.make ?? null,
      model: vehicle.model ?? null,
      size: vehicle.size ?? null,
      type: vehicle.type ?? null,
      miles: vehicle.miles ?? null,
      fuel: vehicle.fuel ?? null,
      condition: vehicle.condition ?? null,
      tier: vehicle.tier ?? null,
      color: vehicle.color ?? null,
      rear_wheel: vehicle.rear_wheel ?? null,

      optic_list_price: toNumberOrNull(vehicle.optic_list_price),
      seller_asking_price: toNumberOrNull(vehicle.seller_asking_price),
      profit: toNumberOrNull(vehicle.profit),
      profit_realized: vehicle.profit_realized === true,
      lien_payoff_amount: toNumberOrNull(vehicle.lien_payoff_amount),
      repair_cost: toNumberOrNull(vehicle.repair_cost),
      estimated_repair_cost: toNumberOrNull(vehicle.estimated_repair_cost),
      shipping_cost: toNumberOrNull(vehicle.shipping_cost),

      status: vehicle.status ?? null,
      sold_date: vehicle.sold_date ?? null,
      who: vehicle.who ?? null,
      bought_from: vehicle.bought_from ?? null,
      sold_to_name: vehicle.sold_to_user?.name ?? '',
      sold_by_name: vehicle.sold_by_user?.name ?? '',
      selling_to_name: vehicle.selling_to_user?.name ?? '',

      location: vehicle.location ?? null,
      phone_number: vehicle.phone_number ?? null,
      email: vehicle.email ?? null,
      truck_notes: vehicle.truck_notes ?? null,
      follow_up_notes: vehicle.follow_up_notes ?? null,
      link: vehicle.link ?? null,
      title_state: vehicle.title_state ?? null,

      mechanical_status: vehicle.mechanical_status ?? null,
      body_status: vehicle.body_status ?? null,
      cameras: vehicle.cameras ?? 'unknown',
      shelves: vehicle.shelves ?? 'unknown',
      gwvr_over_10k: vehicle.gwvr_over_10k ?? 'unknown',

      show_on_website: vehicle.show_on_website === true,
      favorite: vehicle.favorite === true,
      paid_for: vehicle.paid_for === true,
      ready_for_sale: vehicle.ready_for_sale === true,
      funded: vehicle.funded === true,
      size_confirmed: vehicle.size_confirmed === true,
      refinance: vehicle.refinance === true,

      pickup_address:
        vehicle.refinance === true
          ? 'N/A'
          : isPickupAddressComplete(vehicle)
            ? 'Complete'
            : 'Incomplete',
      ready_for_transport: vehicle.ready_for_transport?.ready_for_pickup === true ? 'Ready' : 'Not Ready',
      delivered_to_customer: isNotApplicableDelivery
        ? 'N/A'
        : vehicle.delivered_to_customer === true
          ? 'Yes'
          : 'No',

      grayBookPrice,
      ltv,
      dealQuality,

      safety: safetyByVehicleId.get(vehicle.id) ?? DEFAULT_SAFETY_COLORS,
      isInPackage: timeline?.isInPackage ?? false,
      isDelivered: timeline?.isDelivered ?? false,
    }
  })

/** Match Inventory v2 default: newest `date_added` first. */
export const sortInventoryV3RowsByDateAddedDesc = (rows: InventoryV3Row[]): InventoryV3Row[] =>
  [...rows].sort((left, right) => {
    const leftTime = left.date_added ? new Date(left.date_added).getTime() : 0
    const rightTime = right.date_added ? new Date(right.date_added).getTime() : 0
    return rightTime - leftTime
  })
